import type { KeyboardEvent } from 'react'
import type { IPatientConsultationStaff } from 'features/Home/interfaces/IInfoPatient'
import {
  getScheduleStaffColor,
  getScheduleStaffInitials,
  getScheduleStaffShortName,
} from './scheduleInstance'
import styles from './scheduleDoctorRail.module.scss'

type ScheduleDoctorRailProps = {
  staff: IPatientConsultationStaff[]
  focusedStaffIds: number[]
  onToggleStaff: (staffId: number) => void
  onClear: () => void
}

export const ScheduleDoctorRail = ({
  staff,
  focusedStaffIds,
  onToggleStaff,
  onClear,
}: ScheduleDoctorRailProps) => {
  const hasFocus = focusedStaffIds.length > 0
  const focused = new Set(focusedStaffIds)

  const handleKeyDown = (staffId: number) => (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    onToggleStaff(staffId)
  }

  return (
    <aside className={styles.rail} aria-label='Filter schedule by doctor'>
      <div className={styles.head}>Dr</div>
      <div className={styles.list}>
        {staff.map((member) => {
          const color = getScheduleStaffColor(member.id)
          const active = focused.has(member.id)
          const muted = hasFocus && !active
          return (
            <button
              key={member.id}
              type='button'
              className={styles.tab}
              style={{
                background: active ? color : '#fff',
                borderColor: active ? color : undefined,
                opacity: muted ? 0.4 : 1,
                ['--tab-ink' as string]: active ? '#fff' : 'var(--dark-04)',
              }}
              aria-pressed={active}
              onClick={() => onToggleStaff(member.id)}
              onKeyDown={handleKeyDown(member.id)}>
              <span className={styles.bar} style={{ background: color }} />
              <span className={styles.name}>{getScheduleStaffShortName(member.name)}</span>
              <span
                className={styles.initials}
                style={{
                  background: active ? '#fff' : 'var(--light-02)',
                  color: active ? color : 'var(--dark-04)',
                }}>
                {getScheduleStaffInitials(member.name)}
              </span>
            </button>
          )
        })}
      </div>
      <div className={styles.footer}>
        <button
          type='button'
          className={hasFocus ? `${styles.clear} ${styles.clearActive}` : styles.clear}
          onClick={onClear}
          disabled={!hasFocus}
          title='Clear provider filter'
          aria-label='Clear provider filter'>
          ✕
        </button>
      </div>
    </aside>
  )
}
